import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import { useAuth } from './auth/AuthContext';

const LoginButton = () => {
  const { googleLogin, sendLoginCode, verifyLoginCode } = useAuth();
  const googleButtonRef = useRef(null);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || !googleButtonRef.current) return undefined;

    let cancelled = false;
    let script = document.querySelector('script[data-google-identity]');

    const initialize = () => {
      if (cancelled || !window.google?.accounts?.id || !googleButtonRef.current) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          setBusy(true);
          setError('');
          try {
            await googleLogin(response.credential);
          } catch (requestError) {
            setError(requestError.message);
          } finally {
            setBusy(false);
          }
        },
        auto_select: false
      });
      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        width: 420
      });
    };

    if (!script) {
      script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.dataset.googleIdentity = 'true';
      document.head.appendChild(script);
    }

    if (window.google?.accounts?.id) initialize();
    else script.addEventListener('load', initialize, { once: true });

    return () => {
      cancelled = true;
      script?.removeEventListener('load', initialize);
    };
  }, [googleLogin]);

  const requestCode = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await sendLoginCode(email.trim());
      setChallengeId(result.challengeId);
      setMessage(result.message);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    setBusy(true);
    setError('');
    try {
      await verifyLoginCode(email.trim(), code.trim(), challengeId);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = (event) => {
    event.preventDefault();
    if (!busy && email.trim() && (!challengeId || code.length === 6)) {
      if (challengeId) verifyCode();
      else requestCode();
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(360px, 0.9fr) minmax(500px, 1.1fr)' },
        bgcolor: '#f5f7fb'
      }}
    >
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: { md: 6, lg: 9 },
          color: 'common.white',
          background: 'linear-gradient(145deg, #091b33 0%, #0c3d66 55%, #087e8b 100%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            width: 420,
            height: 420,
            borderRadius: '50%',
            top: -180,
            right: -130,
            background: 'rgba(76, 201, 240, 0.12)',
            border: '1px solid rgba(255,255,255,0.12)'
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            width: 300,
            height: 300,
            borderRadius: '50%',
            bottom: -130,
            left: -80,
            background: 'rgba(255, 183, 77, 0.10)'
          }
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: 2.5,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(255,255,255,0.14)',
              border: '1px solid rgba(255,255,255,0.2)',
              backdropFilter: 'blur(8px)'
            }}
          >
            <PhoneInTalkRoundedIcon />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={800} lineHeight={1.1}>Route My Call</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.68)', letterSpacing: 1.2 }}>
              BUSINESS COMMUNICATIONS
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 540, my: 8 }}>
          <Typography
            component="h1"
            sx={{ fontSize: { md: '2.7rem', lg: '3.5rem' }, fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.035em' }}
          >
            Your business calls,
            <Box component="span" sx={{ display: 'block', color: '#70d6e0' }}>beautifully connected.</Box>
          </Typography>
          <Typography sx={{ mt: 3, maxWidth: 470, color: 'rgba(255,255,255,0.72)', fontSize: '1.08rem', lineHeight: 1.75 }}>
            Manage calls, messages, recordings, and customer conversations from one secure workspace.
          </Typography>
          <Stack spacing={2} sx={{ mt: 5 }}>
            {['Secure passwordless access', 'One place for every conversation', 'Built for teams that stay responsive'].map((item) => (
              <Stack key={item} direction="row" spacing={1.5} alignItems="center">
                <CheckCircleOutlineRoundedIcon sx={{ color: '#70d6e0', fontSize: 21 }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.88)' }}>{item}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Typography variant="caption" sx={{ position: 'relative', zIndex: 1, color: 'rgba(255,255,255,0.48)' }}>
          Reliable communication for modern businesses
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2.5, sm: 5, lg: 8 },
          background: {
            xs: 'linear-gradient(160deg, #eef8fa 0%, #f7f8fc 45%, #f5f7fb 100%)',
            md: '#f5f7fb'
          }
        }}
      >
        <Paper
          elevation={0}
          component="main"
          sx={{
            width: '100%',
            maxWidth: 480,
            p: { xs: 3, sm: 5 },
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'rgba(15, 55, 82, 0.09)',
            boxShadow: '0 24px 70px rgba(19, 54, 76, 0.11)'
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ display: { xs: 'flex', md: 'none' }, mb: 4 }}>
            <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#0c5273', color: 'white' }}>
              <PhoneInTalkRoundedIcon fontSize="small" />
            </Box>
            <Typography variant="h6" fontWeight={800} color="#102c44">Route My Call</Typography>
          </Stack>

          <Box sx={{ width: 52, height: 52, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: '#e7f4f6', color: '#087e8b', mb: 3 }}>
            {challengeId ? <LockOutlinedIcon /> : <SecurityRoundedIcon />}
          </Box>
          <Typography component="h2" variant="h4" fontWeight={800} color="#102c44" letterSpacing="-0.025em">
            {challengeId ? 'Check your inbox' : 'Welcome back'}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1.25, mb: 3.5, lineHeight: 1.65 }}>
            {challengeId
              ? <>Enter the six-digit code sent to <Box component="span" fontWeight={700} color="text.primary">{email.trim()}</Box>.</>
              : 'Enter your account email and we’ll send you a secure, one-time login code.'}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}
          {message && challengeId && <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>{message}</Alert>}

          <Box sx={{ display: 'none' }}>
            <Box ref={googleButtonRef} sx={{ minHeight: 40 }} />
            <Divider>or use an email code</Divider>
          </Box>

          <Box component="form" onSubmit={submit} noValidate>
            <Stack spacing={2.25}>
              <TextField
                label="Email address"
                type="email"
                autoComplete="email"
                autoFocus={!challengeId}
                fullWidth
                value={email}
                disabled={busy || Boolean(challengeId)}
                onChange={(event) => setEmail(event.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EmailOutlinedIcon fontSize="small" /></InputAdornment>
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.25 } }}
              />
              {challengeId && (
                <TextField
                  label="Verification code"
                  autoFocus
                  fullWidth
                  autoComplete="one-time-code"
                  inputProps={{ inputMode: 'numeric', maxLength: 6, style: { letterSpacing: '0.48em', fontSize: '1.25rem', fontWeight: 700 } }}
                  value={code}
                  disabled={busy}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.25 } }}
                />
              )}
              <Button
                type="submit"
                size="large"
                variant="contained"
                disableElevation
                disabled={busy || !email.trim() || (Boolean(challengeId) && code.length !== 6)}
                sx={{
                  minHeight: 52,
                  borderRadius: 2.25,
                  fontWeight: 750,
                  textTransform: 'none',
                  fontSize: '1rem',
                  bgcolor: '#0c5273',
                  '&:hover': { bgcolor: '#083e58' }
                }}
              >
                {busy ? <CircularProgress size={24} color="inherit" /> : challengeId ? 'Verify and continue' : 'Send my login code'}
              </Button>
            </Stack>
          </Box>

          {challengeId && (
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="center" alignItems="center" spacing={{ xs: 0, sm: 1 }} sx={{ mt: 2 }}>
              <Button size="small" disabled={busy} onClick={requestCode} sx={{ textTransform: 'none' }}>Resend code</Button>
              <Typography color="text.disabled" sx={{ display: { xs: 'none', sm: 'block' } }}>•</Typography>
              <Button
                size="small"
                disabled={busy}
                sx={{ textTransform: 'none' }}
                onClick={() => { setChallengeId(''); setCode(''); setMessage(''); setError(''); }}
              >
                Use a different email
              </Button>
            </Stack>
          )}

          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 4, color: 'text.secondary' }}>
            <LockOutlinedIcon sx={{ fontSize: 15 }} />
            <Typography variant="caption">Secure, passwordless sign-in</Typography>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default LoginButton;
